import { CommandMan, DuctType, type Damper } from "@eisox/api/core-api";
import { ActionButton, Modal } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { DamperIcon, PencilIcon } from "@eisox/icons";
import { getDirtyValues } from "@eisox/tools";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import { Mode } from "~/components/live-history-toggle";
import { m } from "~/paraglide/messages";

import { useUpdateAirHandlerUnit } from "../hooks/use-update-air-handler-unit";
import { useAirHandlerUnitContext } from "../providers/air-handler-unit.provider";
import * as ActuatorDialog from "./actuator-dialog";

const NAME = "DamperDialog";

const DAMPER_TITLE: Record<DuctType, () => string> = {
  [DuctType.EXHAUST]: m.air_handler_unit_damper_exhaust,
  [DuctType.RETURN]: m.air_handler_unit_damper_return,
  [DuctType.SUPPLY]: m.air_handler_unit_damper_supply,
  [DuctType.OUTSIDE]: m.air_handler_unit_damper_outside,
};

/** A damper without a duct `type` is a recirculation damper (it hangs directly off the AHU). */
const damperTitle = (type: DuctType | undefined): string =>
  type === undefined ? m.air_handler_unit_damper_recirculation() : DAMPER_TITLE[type]();

interface CommandFormValues {
  id: string;
  command: number | undefined;
  forcing: number | undefined;
}

interface DamperDialogProps extends Modal.RootProps {
  buildingId: string;
  airHandlerUnitId: string;
  damper: Damper;
  children?: React.ReactNode;
}

const DamperDialog: React.FC<DamperDialogProps> = ({
  buildingId,
  airHandlerUnitId,
  damper,
  open: openProp,
  onOpenChange,
  defaultOpen,
  children,
}) => {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen ?? false,
  });

  const { mode } = useAirHandlerUnitContext(NAME);
  const disabled = mode === Mode.History;

  const { mutateAsync } = useUpdateAirHandlerUnit(buildingId);

  const defaultValues: CommandFormValues = { id: damper.id, command: damper.command, forcing: damper.forcing };
  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value, formApi }) => {
      if (disabled) return;
      const dirty = getDirtyValues(formApi.options.defaultValues, value, { identity: "id" });
      if (!dirty) return;
      await mutateAsync({ airHandlerUnitId, body: { dampers: [dirty] } });
      setOpen(false);
    },
  });

  // The form outlives the modal content, so reopening must restore the actuator's current values.
  const handleOpenChange = (next: boolean) => {
    if (next) form.reset();
    setOpen(next);
  };

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange}>
      {children && <Modal.Trigger asChild>{children}</Modal.Trigger>}
      <Modal.Content>
        <form
          onSubmit={e => {
            e.preventDefault();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <Modal.Header
              title={damperTitle(damper.type)}
              subtitle={m.air_handler_unit_actuator_dialog_subtitle()}
              icon={<DamperIcon />}
            >
              <Modal.Close asChild>
                <ActionButton variant="cancel">{m.cancel()}</ActionButton>
              </Modal.Close>
              {!disabled && <form.SubmitButton label={m.save()} icon={<PencilIcon />} />}
            </Modal.Header>
            <ActuatorDialog.Group label={m.air_handler_unit_damper()}>
              {(damper.command !== undefined || damper.commandMan === CommandMan.MANUAL) && (
                <form.AppField name="command">
                  {commandField => (
                    <ActuatorDialog.Item label={m.control()}>
                      <ActuatorDialog.Command
                        commandMan={damper.commandMan}
                        value={commandField.state.value}
                        onValueChange={value => commandField.handleChange(value)}
                        disabled={disabled}
                      />
                      <form.AppField name="forcing">
                        {forcingField => (
                          <ActuatorDialog.Forcing
                            type="open/close"
                            command={commandField.state.value}
                            commandMan={damper.commandMan}
                            commandType={damper.commandType}
                            value={forcingField.state.value}
                            onValueChange={value => forcingField.handleChange(value)}
                            disabled={disabled}
                          />
                        )}
                      </form.AppField>
                    </ActuatorDialog.Item>
                  )}
                </form.AppField>
              )}
              <ActuatorDialog.Item label={m.state()}>
                <ActuatorDialog.ActuatorState
                  type="open/close"
                  commandType={damper.commandType}
                  state={damper.state}
                  commandAutomate={damper.commandAutomate}
                  commandMan={damper.commandMan}
                  defect={!!damper.defect}
                  missmatchDefect={!!damper.missmatchDefect}
                />
              </ActuatorDialog.Item>
            </ActuatorDialog.Group>
          </form.AppForm>
        </form>
      </Modal.Content>
    </Modal.Root>
  );
};

DamperDialog.displayName = NAME;

export { DamperDialog };
export type { DamperDialogProps };
